// =====================================================
// LED 产品 5 级层级类型定义
// 参考 Apple 产品层级设计
// =====================================================

// =====================================================
// Level 1: 产品分类 (Category)
// =====================================================

/** 产品分类 */
export type ProductCategory = 'all_in_one' | 'product' | 'module';

/** 分类显示名称 */
export const CategoryLabels: Record<ProductCategory, string> = {
  all_in_one: '一体机',
  product: '产品',
  module: '模组',
};

/** 分类图标颜色 */
export const CategoryColors: Record<ProductCategory, { bg: string; text: string }> = {
  all_in_one: { bg: 'bg-purple-100', text: 'text-purple-700' },
  product: { bg: 'bg-blue-100', text: 'text-blue-700' },
  module: { bg: 'bg-orange-100', text: 'text-orange-700' },
};

// =====================================================
// Level 2: 产品系列 (Series)
// =====================================================

/** 产品系列 */
export interface ProductSeries {
  id: string;
  name: string;
  name_en?: string;
  category: ProductCategory;
  description?: string;
  image_url?: string;
  image_storage_key?: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // 聚合数据（从视图获取）
  family_count?: number;
  model_count?: number;
}

/** 创建系列的数据 */
export type CreateSeriesData = Omit<ProductSeries, 'id' | 'created_at' | 'updated_at' | 'family_count' | 'model_count'>;

/** 更新系列的数据 */
export type UpdateSeriesData = Partial<CreateSeriesData>;

// =====================================================
// Level 3: 产品族 (Family)
// =====================================================

/** 产品族 */
export interface ProductFamily {
  id: string;
  series_id: string;
  name: string;
  name_en?: string;
  slogan?: string;
  description?: string;
  image_url?: string;
  image_storage_key?: string;
  features?: string[];
  process_type?: string;  // COB / SMD / MIP
  sort_order: number;
  is_active: boolean;
  is_featured: boolean;
  created_at: string;
  updated_at: string;
  // 聚合数据
  model_count?: number;
  sku_count?: number;
  pitch_range?: string;  // 如 "P0.7-P1.8"
  // 关联数据
  series?: ProductSeries;
  series_name?: string;
  series_category?: ProductCategory;
}

/** 创建产品族的数据 */
export type CreateFamilyData = Omit<ProductFamily, 'id' | 'created_at' | 'updated_at' | 'model_count' | 'sku_count' | 'pitch_range' | 'series' | 'series_name' | 'series_category'>;

/** 更新产品族的数据 */
export type UpdateFamilyData = Partial<CreateFamilyData>;

// =====================================================
// Level 4: 产品型号 (Model)
// =====================================================

/** 产品型号 */
export interface ProductModelV2 {
  id: string;
  family_id: string;
  name: string;
  name_en?: string;
  // 原有字段
  category: ProductCategory;
  series_name?: string;
  image_url?: string;
  image_storage_key?: string;
  pitch_range?: string;
  size_specs?: string;
  process_type?: string;
  features?: string[];
  slogan?: string;
  // 新增型号级字段
  pitch?: number;          // 具体间距值 (mm)
  led_type?: string;       // LED 类型: COB / SMD / MIP
  maintenance?: string;    // 维护方式: Front / Rear
  environment?: string;    // 使用环境: indoor / outdoor
  material?: string;       // 材质: Die-cast Aluminum
  curve_support?: boolean; // 是否支持曲面
  sort_order: number;
  is_active: boolean;
  is_featured: boolean;
  created_at: string;
  updated_at: string;
  // 聚合数据
  sku_count?: number;
  // 关联数据
  family?: ProductFamily;
  skus?: ProductSKU[];
}

/** 创建型号的数据 */
export type CreateModelData = Omit<ProductModelV2, 'id' | 'created_at' | 'updated_at' | 'sku_count' | 'family' | 'skus'>;

/** 更新型号的数据 */
export type UpdateModelData = Partial<CreateModelData>;

// =====================================================
// Level 5: SKU/变体 (SKU)
// =====================================================

/** 产品 SKU/变体 */
export interface ProductSKU {
  id: string;
  product_id: string;      // 关联到 Model
  model_name: string;      // SKU 名称，如 "600nit 600×337.5mm"
  // 原有字段
  pitch?: number;
  size_option?: string;
  cabinet_size?: string;
  weight?: string;
  power_consumption?: string;
  specs?: Record<string, any>;
  is_default: boolean;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
  // 新增 SKU 级字段
  brightness_typical?: string;   // 典型亮度: "600"
  brightness_max?: string;       // 最大亮度: "1000"
  panel_width?: number;          // 箱体宽度 (mm)
  panel_height?: number;         // 箱体高度 (mm)
  panel_depth?: number;          // 箱体深度 (mm)
  module_width?: number;         // 模组宽度 (mm)
  module_height?: number;        // 模组高度 (mm)
  max_power?: number;            // 最大功耗 (W)
  typical_power?: number;        // 典型功耗 (W)
  refresh_rate?: number;         // 刷新率 (Hz)
  ingress_protection?: string;   // 防护等级: IP50, IP65
  processing_depth?: string;     // 处理深度: 19, 21 bit
  viewing_angle?: string;        // 视角: "H170°/V160°"
  contrast_ratio?: string;       // 对比度: "10000:1"
  color_temperature?: string;    // 色温范围
  input_voltage?: string;        // 输入电压
  wiring_direction?: string;     // 布线方向
  power_direction?: string;      // 电源方向
}

/** 创建 SKU 的数据 */
export type CreateSKUData = Omit<ProductSKU, 'id' | 'created_at' | 'updated_at'>;

/** 更新 SKU 的数据 */
export type UpdateSKUData = Partial<CreateSKUData>;

// =====================================================
// 导航类型
// =====================================================

/** 面包屑导航项 */
export interface BreadcrumbItem {
  level: 1 | 2 | 3 | 4 | 5;
  type: 'category' | 'series' | 'family' | 'model' | 'sku';
  id?: string;
  name: string;
}

/** 层级路径 */
export interface HierarchyPath {
  category?: ProductCategory;
  seriesId?: string;
  familyId?: string;
  modelId?: string;
  skuId?: string;
}

// =====================================================
// 树形视图类型
// =====================================================

/** 树形节点 */
export interface TreeNode {
  id: string;
  name: string;
  level: 1 | 2 | 3 | 4 | 5;
  type: 'category' | 'series' | 'family' | 'model' | 'sku';
  children?: TreeNode[];
  isExpanded?: boolean;
  isSelected?: boolean;
  isLoading?: boolean;
  // 额外数据
  data?: {
    category?: ProductCategory;
    pitch?: number;
    count?: number;
  };
}

/** 树形视图状态 */
export interface TreeViewState {
  expandedNodes: Set<string>;
  selectedNodeId: string | null;
  loadingNodes: Set<string>;
}

// =====================================================
// 视图模式
// =====================================================

/** 导航视图模式 */
export type ViewMode = 'breadcrumb' | 'tree';

// =====================================================
// 筛选和排序
// =====================================================

/** 层级筛选条件 */
export interface HierarchyFilter {
  category?: ProductCategory;
  search?: string;
  isActive?: boolean;
  isFeatured?: boolean;
}

/** 排序字段 */
export type HierarchySortField = 'name' | 'created_at' | 'sort_order';

/** 排序方向 */
export type SortOrder = 'asc' | 'desc';

// =====================================================
// CSV 导入类型
// =====================================================

/** CSV 产品行数据 */
export interface CSVProductRow {
  'Product Family': string;
  'Model Name': string;
  'Product Type': string;
  'Pixel Pitch (mm)': string;
  'Brightness Typical': string;
  'Max Power Per Panel (W)': string;
  'Typical Power Per Panel (W)': string;
  'Weight (kg/panel)': string;
  'Panel Width (mm)': string;
  'Panel Height (mm)': string;
  'Panel Depth (mm)': string;
  'Module Width (mm)': string;
  'Module Height (mm)': string;
  'Refresh Rate (Hz)': string;
  'LED Type': string;
  'Maintenance': string;
  'Ingress Protection': string;
  'Environment': string;
  'Material': string;
  'Wiring Direction': string;
  'Power Direction': string;
  'Processing Depth (bit)': string;
  'Viewing Angle': string;
  'Contrast Ratio': string;
  'Color Temperature': string;
  'Input Voltage': string;
}

/** 导入结果 */
export interface ImportResult {
  success: boolean;
  seriesCreated: number;
  familiesCreated: number;
  modelsCreated: number;
  skusCreated: number;
  errors: string[];
}

// =====================================================
// API 响应类型
// =====================================================

/** 带分页的列表响应 */
export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

/** 层级统计 */
export interface HierarchyStats {
  seriesCount: number;
  familyCount: number;
  modelCount: number;
  skuCount: number;
  byCategory: Record<ProductCategory, {
    seriesCount: number;
    familyCount: number;
    modelCount: number;
  }>;
}
